import {
  isNormalPositive,
  useWallet,
} from '@bifrost-platform/bifront-sdk-react-wallet';
import { motion } from 'framer-motion';
import { useAtom } from 'jotai';
import { Trans, useTranslation } from 'next-i18next';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import IconHelp from '@/assets/icons/icon-help.svg';
import ButtonWithArrow from '@/components/ButtonWithArrow';
import LTVDetailInfo from '@/components/DashBoard/LTVDetailInfo';
import Button from '@/components/atom/Button';
import { FlexBox } from '@/components/atom/FlexBox';
import { GlassPanel } from '@/components/atom/GlassPanel';
import { StyledText } from '@/components/atom/Text';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@/components/atom/Tooltip';
import useBtcfi from '@/hooks/api/useBtcfi';
import useInitializer from '@/hooks/bifrontSdk/useInitializer';
import useGetCurrencyString from '@/hooks/store/useGetCurrencyString';
import useEnv from '@/hooks/useEnv';
import {
  formatAmount,
  formatAmountOnHover,
  formatNumber,
} from '@/libs/formatNumber';
import { eventFirstDepositClick } from '@/libs/gtag';
import { dialogActionAtom, isShowConnectWalletAtom } from '@/store/uiStore';
import { dashboardCardVariant } from '@/styles/motion';
import TokenIcon from '../TokenIcon';
import Skeleton from '../atom/Skeleton';

const Container = styled(GlassPanel)`
  width: 504px;
  flex-shrink: 0;
`;

const InfoPanel = styled(FlexBox)`
  flex: 1;
`;

const HelpIcon = styled(IconHelp)`
  width: 20px;
  height: 20px;
  fill: ${(p) => p.theme.colors.textGray};
`;

const RemainingText = styled(GlassPanel)`
  align-self: flex-end;
  ${(p) => p.theme.text.md};
  padding: 8px 12px;

  & > strong {
    color: ${(p) => p.theme.colors.green};
    ${(p) => p.theme.font.bold};
  }
`;

const ButtonPanel = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  ${(p) => p.theme.gap.md}
`;

interface Props {
  isMobile: boolean;
}

export default function MintedBtcUsdPanel({ isMobile }: Props) {
  // translation
  const { t } = useTranslation('dashboard');

  // store
  const [, setIsShowConnectWallet] = useAtom(isShowConnectWalletAtom);

  // initializer
  const {
    syncedEnv,
    syncedChains,
    syncedBtcfiHandlers,
    syncedPairs,
    syncedBtcfi,
  } = useInitializer();

  // env
  const { isTestnet, tokenBifrostBtcusd, languageId } = useEnv();

  // wallet
  const { account } = useWallet();

  // btcfi
  const {
    statusBtcusd,
    borrowAmount,
    borrowAmountValue,
    depositAmountValue,
    borrowableAmountValue,
    isLoading: isLoadingBtcfi,
  } = useBtcfi();

  // currency
  const { getCurrencyString, getNumberWithCurrencyReplace } =
    useGetCurrencyString();

  // store
  const [, setDialogAction] = useAtom(dialogActionAtom);

  // memo
  const synced = useMemo(
    () =>
      syncedEnv &&
      syncedChains &&
      syncedBtcfiHandlers &&
      syncedPairs &&
      syncedBtcfi,
    [syncedEnv, syncedChains, syncedBtcfiHandlers, syncedPairs, syncedBtcfi]
  );
  const isLoading = useMemo(
    () => !!(account && !synced && isLoadingBtcfi),
    [account, synced, isLoadingBtcfi]
  );
  const isFirstTime = useMemo(
    () =>
      account &&
      !isNormalPositive(depositAmountValue, true) &&
      !isNormalPositive(borrowableAmountValue, true),
    [account, depositAmountValue, borrowableAmountValue]
  );
  const maxBorrowAmount = useMemo(
    () => formatNumber(statusBtcusd?.maxAmount?.borrow ?? '0'),
    [statusBtcusd?.maxAmount?.borrow]
  );
  const isDisabledByWallet = useMemo(() => !account, [account]);
  const isDisabledRepay = useMemo(
    () => !(!isDisabledByWallet && isNormalPositive(borrowAmount, true)),
    [borrowAmount, isDisabledByWallet]
  );
  const isDisabledBorrow = useMemo(
    () => !(!isDisabledByWallet && isNormalPositive(maxBorrowAmount, true)),
    [isDisabledByWallet, maxBorrowAmount]
  );

  // handler
  const handleRepayClick = useCallback(
    () =>
      setDialogAction({ isShow: true, type: isMobile ? 'mobile' : 'repay' }),
    [isMobile, setDialogAction]
  );
  const handleMintClick = useCallback(() => {
    if (!isMobile && isDisabledRepay && isDisabledBorrow) {
      eventFirstDepositClick(isTestnet);
    }

    setDialogAction({
      isShow: true,
      type: isMobile
        ? 'mobile'
        : isDisabledRepay && isDisabledBorrow
          ? 'deposit'
          : 'borrow',
    });
  }, [setDialogAction, isMobile, isDisabledRepay, isDisabledBorrow, isTestnet]);
  const handleDisabledSupplyClick = useCallback(
    () => setIsShowConnectWallet(true),
    [setIsShowConnectWallet]
  );

  return (
    <Container
      as={motion.div}
      variants={dashboardCardVariant}
      $padding="xl"
      $flex
      $direction="column"
      $gap="xl"
      $rounded="xl"
    >
      <FlexBox $flex $gap="xxs">
        <TokenIcon isHideChain size={24} {...tokenBifrostBtcusd} />
        <StyledText as="h3" $fontSize="md" $fontWeight="medium">
          {t('mintedBtcUsd')}
        </StyledText>
      </FlexBox>
      <InfoPanel $flex $direction="column" $gap="xs">
        <FlexBox $flex $direction="column" $grow={1}>
          {isLoading ? (
            <Skeleton $height={76} />
          ) : (
            <>
              <StyledText $fontSize="xxxl" $fontWeight="black">
                <Tooltip placement="top" offset={16}>
                  <TooltipTrigger asChild>
                    {formatAmount(borrowAmount)}
                  </TooltipTrigger>
                  <TooltipContent>
                    <TooltipArrow />
                    <StyledText $fontSize="xs" $fontWeight="medium">
                      {formatAmountOnHover(borrowAmount)}{' '}
                      {tokenBifrostBtcusd?.symbol ?? 'BtcUSD'}
                    </StyledText>
                  </TooltipContent>
                </Tooltip>{' '}
                {tokenBifrostBtcusd?.symbol ?? 'BtcUSD'}
              </StyledText>
              <StyledText
                $fontSize="lg"
                $fontWeight="medium"
                $textColor="textGray"
              >
                {getNumberWithCurrencyReplace(borrowAmountValue)}
              </StyledText>
            </>
          )}
        </FlexBox>
        {!isDisabledByWallet &&
          !isFirstTime &&
          (isLoading ? (
            <Skeleton
              $alignSelf="flex-end"
              $width={289}
              $height={40}
              $rounded="xs"
            />
          ) : (
            <>
              <RemainingText
                $padding="xxs"
                $flex
                $gap="xxxs"
                $noBorder
                $rounded="md"
                $align="center"
              >
                <Trans
                  t={t}
                  i18nKey="remainingMint"
                  values={{
                    amount: getCurrencyString(
                      maxBorrowAmount ?? '0',
                      tokenBifrostBtcusd
                    ),
                  }}
                />
                <Tooltip placement="top-end" offset={14}>
                  <TooltipTrigger>
                    <HelpIcon />
                  </TooltipTrigger>
                  <TooltipContent
                    $padding={{ y: 'xs', x: 'md' }}
                    style={{ width: languageId === 'ko' ? '305px' : '345px' }}
                  >
                    <TooltipArrow />
                    <LTVDetailInfo />
                  </TooltipContent>
                </Tooltip>
              </RemainingText>
            </>
          ))}
        <ButtonPanel>
          <Button
            $variant="secondary"
            $rounded="lg"
            $padding={{
              x: 'xl',
              y: 'md',
            }}
            $fontWeight="bold"
            disabled={isDisabledRepay}
            onClick={handleRepayClick}
          >
            {t('repay')}
          </Button>
          {isDisabledByWallet ? (
            <Tooltip placement="top" offset={16}>
              <TooltipTrigger asChild>
                <ButtonWithArrow
                  rounded="lg"
                  padding={{
                    x: 'xl',
                    y: 'md',
                  }}
                  fontWeight="bold"
                  variant="primary"
                  onClick={handleDisabledSupplyClick}
                >
                  {t('startNow')}
                </ButtonWithArrow>
              </TooltipTrigger>
              <TooltipContent>
                <TooltipArrow />
                <StyledText $fontSize="md" $fontWeight="bold">
                  {t('connectWalletFirst')}
                </StyledText>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Button
              $rounded="lg"
              $padding={{
                x: 'xl',
                y: 'md',
              }}
              $fontWeight="bold"
              $variant="primary"
              onClick={handleMintClick}
            >
              {t(isDisabledRepay && isDisabledBorrow ? 'startNow' : 'mint')}
            </Button>
          )}
        </ButtonPanel>
      </InfoPanel>
    </Container>
  );
}
